import { idZodSchema } from "@@/shared/id";
import { hexColorZodSchema } from "@@/shared/stylable/hex-color/hex-color";
import { languageCodeCsvZodSchema } from "@@/shared/translations/language-code";
import {
    asTranslatableValue,
    requiredTranslatableZodSchema,
} from "@@/shared/translations/translation";
import { generateId } from "@@/utilities/generate-id";
import type { Except } from "type-fest";
import { z } from "zod";
import { pageBlockZodSchema } from "../page-blocks/page-block";
import { pageBlockIdZodSchema } from "../page-blocks/page-block-id";
import type { PageId} from "../page-id";
import { pageIdZodSchema } from "../page-id";
import { pageSlugZodSchema } from "../page-slug";
import { pageStateZodSchema } from "../page-state";

const pageEditCommandZodSchema = z.object({
    _id: idZodSchema,
    _type: z.literal("PAGE_EDIT_COMMAND"),
    title: requiredTranslatableZodSchema,
    backgroundColor: hexColorZodSchema.optional(),
    pageInnerWidth: z.number().optional(),
    pageMaxWidth: z.number().optional(),
    pageId: pageIdZodSchema,
    state: pageStateZodSchema.optional(),
    slug: pageSlugZodSchema.optional(),
    categories: z.record(z.string(), z.boolean()).optional(),
    tags: z.record(z.string(), z.boolean()).optional(),
    areas: z.record(z.string(), z.boolean()).optional(),
    fallbackPageId: pageIdZodSchema.nullish(),
    blocks: z.record(pageBlockIdZodSchema, pageBlockZodSchema.nullish()),
    blockSortOrder: z.array(pageBlockIdZodSchema),
    languageCodeCsv: languageCodeCsvZodSchema,
    branding: z
        .object({
            borderRadius: z.number().optional(),
        })
        .optional(),
});
type PageEditCommand = z.infer<typeof pageEditCommandZodSchema>;

type PageEditCommandFactoryParams = Except<PageEditCommand, "_id" | "_type">;
const pageEditCommandFactory = (
    params: PageEditCommandFactoryParams,
): PageEditCommand => {
    const data: PageEditCommand = {
        ...params,
        _id: generateId("page_edit_command__"),
        _type: "PAGE_EDIT_COMMAND",
        title: asTranslatableValue(params.title),
        fallbackPageId: (params.fallbackPageId?.trim() as PageId) || undefined,
        branding: params.branding
            ? {
                  ...params.branding,
                  borderRadius: params.branding.borderRadius,
              }
            : undefined,
    };

    return data;
};

export { pageEditCommandFactory, pageEditCommandZodSchema };
export type { PageEditCommand, PageEditCommandFactoryParams };
